import { defineMessages } from "react-intl";

import {
	aceiActiveIngredientMessages,
	aceiIntakeTypeMessages,
	allopurinolActiveIngredientMessages,
	allopurinolIntakeTypeMessages,
	antiarrhythmicAgentIntakeTypeMessages,
	antibioticIntakeTypeMessages,
	anticoagulantIntakeTypeMessages,
	arbActiveIngredientMessages,
	arbIntakeTypeMessages,
	arniActiveIngredientMessages,
	arniIntakeTypeMessages,
	bblActiveIngredientMessages,
	bblIntakeTypeMessages,
	digitalisIntakeTypeMessages,
	diureticIntakeTypeMessages,
	dobutamineActiveIngredientMessages,
	dobutamineIntakeTypeMessages,
	dopamineActiveIngredientMessages,
	dopamineIntakeTypeMessages,
	ivabradineActiveIngredientMessages,
	ivabradineIntakeTypeMessages,
	levosimendanActiveIngredientMessages,
	levosimendanIntakeTypeMessages,
	mraActiveIngredientMessages,
	mraIntakeTypeMessages,
	norepinephrineActiveIngredientMessages,
	norepinephrineIntakeTypeMessages,
	otherIntakeTypeMessages,
	pdeInhibitorActiveIngredientMessages,
	pdeInhibitorIntakeTypeMessages,
	statinIntakeTypeMessages,
	trombocytaAggregationInhibitorIntakeTypeMessages,
	validator
} from "lib/build/forms/htx/current-cardiological-state/heartFailureMedicalTherapy";

import {
	FieldData,
	FieldGroupListData,
	FieldsetData
} from "src/features/forms";
import { genericErrorMessages } from "src/messages/generic";
import { LeafNode } from "src/modules/shared/form-explorer";

const messages = defineMessages({
	heartFailureMedicalTherapy: {
		id: "form.heart_failure_medical_therapy",
		defaultMessage: "Gyógyszeres kezelés"
	},
	acei: {
		id: "form.heart_failure_medical_therapy.acei",
		defaultMessage: "ACEi"
	},
	bbl: {
		id: "form.heart_failure_medical_therapy.bbl",
		defaultMessage: "BBL"
	},
	arb: {
		id: "form.heart_failure_medical_therapy.arb",
		defaultMessage: "ARB"
	},
	mra: {
		id: "form.heart_failure_medical_therapy.mra",
		defaultMessage: "MRA"
	},
	arni: {
		id: "form.heart_failure_medical_therapy.arni",
		defaultMessage: "ARNI"
	},
	ivabradine: {
		id: "form.heart_failure_medical_therapy.ivabradine",
		defaultMessage: "Ivabradine"
	},
	diuretic: {
		id: "form.heart_failure_medical_therapy.diuretic",
		defaultMessage: "Diureticum"
	},
	antiarrhythmicAgent: {
		id: "form.heart_failure_medical_therapy.antiarrhythmic_agent",
		defaultMessage: "Antiarritmiás szer"
	},
	anticoagulant: {
		id: "form.heart_failure_medical_therapy.anticoagulant",
		defaultMessage: "Anticoaguláns szer"
	},
	trombocytaAggregationInhibitor: {
		id: "form.heart_failure_medical_therapy.trombocyta_aggregation_inhibitor",
		defaultMessage: "Trombocyta aggregáció gátló szer"
	},
	statin: {
		id: "form.heart_failure_medical_therapy.statin",
		defaultMessage: "Sztatin"
	},
	digitalis: {
		id: "form.heart_failure_medical_therapy.digitalis",
		defaultMessage: "Digitálisz"
	},
	antibiotic: {
		id: "form.heart_failure_medical_therapy.antibiotic",
		defaultMessage: "Antibiotikum"
	},
	other: {
		id: "form.heart_failure_medical_therapy.other",
		defaultMessage: "Egyéb"
	},
	activeIngredient: {
		id: "form.heart_failure_medical_therapy.active_ingredient",
		defaultMessage: "Hatóanyag"
	},
	otherIngredientName: {
		id: "form.heart_failure_medical_therapy.otherIngredientName",
		defaultMessage: "Hatóanyag neve"
	},
	dose: {
		id: "form.heart_failure_medical_therapy.dose",
		defaultMessage: "Napi összdózis"
	},
	intakeType: {
		id: "form.heart_failure_medical_therapy.intake_type",
		defaultMessage: "Gyógyszer bevitelének típusa"
	},
	allopurinol: {
		id: "form.heart_failure_medical_therapy.allopurinol",
		defaultMessage: "Allopurinol"
	},
	dopamine: {
		id: "form.heart_failure_medical_therapy.dopamine",
		defaultMessage: "Dopamin"
	},
	dobutamine: {
		id: "form.heart_failure_medical_therapy.dobutamine",
		defaultMessage: "Dobutamin"
	},
	norepinephrine: {
		id: "form.heart_failure_medical_therapy.norepinephrine",
		defaultMessage: "(Nor)adrenalin"
	},
	pdeInhibitor: {
		id: "form.heart_failure_medical_therapy.pde_inhibitor",
		defaultMessage: "PDE inhibitor"
	},
	levosimendan: {
		id: "form.heart_failure_medical_therapy.levosimendan",
		defaultMessage: "Levosimendan"
	}
});

const fields: {
	[key: string]: FieldGroupListData | FieldData;
} = {
	acei: {
		type: "field-group-list",
		path: ["acei"],
		label: messages.acei,
		rows: [
			{
				columns: [
					{
						width: 6,
						field: {
							type: "field",
							path: ["activeIngredient"],
							label: messages.activeIngredient,
							errorMessages: genericErrorMessages,
							input: {
								type: "select",
								props: { labels: aceiActiveIngredientMessages }
							}
						}
					},
					{
						width: 6,
						field: {
							type: "field",
							path: ["otherIngredientName"],
							label: messages.otherIngredientName,
							errorMessages: genericErrorMessages,
							input: { type: "input", props: { type: "text" } }
						}
					}
				]
			},
			{
				columns: [
					{
						width: 6,
						field: {
							type: "field",
							path: ["intakeType"],
							label: messages.intakeType,
							errorMessages: genericErrorMessages,
							input: {
								type: "button-group",
								props: { labels: aceiIntakeTypeMessages }
							}
						}
					},
					{
						width: 6,
						field: {
							type: "field",
							path: ["dose"],
							label: messages.dose,
							errorMessages: genericErrorMessages,
							input: { type: "input", props: { type: "text" } }
						}
					}
				]
			}
		]
	},
	bbl: {
		type: "field-group-list",
		path: ["bbl"],
		label: messages.bbl,
		rows: [
			{
				columns: [
					{
						width: 6,
						field: {
							type: "field",
							path: ["activeIngredient"],
							label: messages.activeIngredient,
							errorMessages: genericErrorMessages,
							input: {
								type: "select",
								props: { labels: bblActiveIngredientMessages }
							}
						}
					},
					{
						width: 6,
						field: {
							type: "field",
							path: ["otherIngredientName"],
							label: messages.otherIngredientName,
							errorMessages: genericErrorMessages,
							input: { type: "input", props: { type: "text" } }
						}
					}
				]
			},
			{
				columns: [
					{
						width: 6,
						field: {
							type: "field",
							path: ["intakeType"],
							label: messages.intakeType,
							errorMessages: genericErrorMessages,
							input: {
								type: "button-group",
								props: { labels: bblIntakeTypeMessages }
							}
						}
					},
					{
						width: 6,
						field: {
							type: "field",
							path: ["dose"],
							label: messages.dose,
							errorMessages: genericErrorMessages,
							input: { type: "input", props: { type: "text" } }
						}
					}
				]
			}
		]
	},
	arb: {
		type: "field-group-list",
		path: ["arb"],
		label: messages.arb,
		rows: [
			{
				columns: [
					{
						width: 6,
						field: {
							type: "field",
							path: ["activeIngredient"],
							label: messages.activeIngredient,
							errorMessages: genericErrorMessages,
							input: {
								type: "select",
								props: { labels: arbActiveIngredientMessages }
							}
						}
					},
					{
						width: 6,
						field: {
							type: "field",
							path: ["otherIngredientName"],
							label: messages.otherIngredientName,
							errorMessages: genericErrorMessages,
							input: { type: "input", props: { type: "text" } }
						}
					}
				]
			},
			{
				columns: [
					{
						width: 6,
						field: {
							type: "field",
							path: ["intakeType"],
							label: messages.intakeType,
							errorMessages: genericErrorMessages,
							input: {
								type: "button-group",
								props: { labels: arbIntakeTypeMessages }
							}
						}
					},
					{
						width: 6,
						field: {
							type: "field",
							path: ["dose"],
							label: messages.dose,
							errorMessages: genericErrorMessages,
							input: { type: "input", props: { type: "text" } }
						}
					}
				]
			}
		]
	},
	mra: {
		type: "field-group-list",
		path: ["mra"],
		label: messages.mra,
		rows: [
			{
				columns: [
					{
						width: 6,
						field: {
							type: "field",
							path: ["activeIngredient"],
							label: messages.activeIngredient,
							errorMessages: genericErrorMessages,
							input: {
								type: "select",
								props: { labels: mraActiveIngredientMessages }
							}
						}
					},
					{
						width: 6,
						field: {
							type: "field",
							path: ["otherIngredientName"],
							label: messages.otherIngredientName,
							errorMessages: genericErrorMessages,
							input: { type: "input", props: { type: "text" } }
						}
					}
				]
			},
			{
				columns: [
					{
						width: 6,
						field: {
							type: "field",
							path: ["intakeType"],
							label: messages.intakeType,
							errorMessages: genericErrorMessages,
							input: {
								type: "button-group",
								props: { labels: mraIntakeTypeMessages }
							}
						}
					},
					{
						width: 6,
						field: {
							type: "field",
							path: ["dose"],
							label: messages.dose,
							errorMessages: genericErrorMessages,
							input: { type: "input", props: { type: "text" } }
						}
					}
				]
			}
		]
	},
	arni: {
		type: "field-group-list",
		path: ["arni"],
		label: messages.arni,
		rows: [
			{
				columns: [
					{
						width: 6,
						field: {
							type: "field",
							path: ["activeIngredient"],
							label: messages.activeIngredient,
							errorMessages: genericErrorMessages,
							input: {
								type: "select",
								props: { labels: arniActiveIngredientMessages }
							}
						}
					},
					{
						width: 6,
						field: {
							type: "field",
							path: ["otherIngredientName"],
							label: messages.otherIngredientName,
							errorMessages: genericErrorMessages,
							input: { type: "input", props: { type: "text" } }
						}
					}
				]
			},
			{
				columns: [
					{
						width: 6,
						field: {
							type: "field",
							path: ["intakeType"],
							label: messages.intakeType,
							errorMessages: genericErrorMessages,
							input: {
								type: "button-group",
								props: { labels: arniIntakeTypeMessages }
							}
						}
					},
					{
						width: 6,
						field: {
							type: "field",
							path: ["dose"],
							label: messages.dose,
							errorMessages: genericErrorMessages,
							input: { type: "input", props: { type: "text" } }
						}
					}
				]
			}
		]
	},
	ivabradine: {
		type: "field-group-list",
		path: ["ivabradine"],
		label: messages.ivabradine,
		rows: [
			{
				columns: [
					{
						width: 12,
						field: {
							type: "field",
							path: ["activeIngredient"],
							label: messages.activeIngredient,
							errorMessages: genericErrorMessages,
							input: {
								type: "select",
								props: {
									labels: ivabradineActiveIngredientMessages
								}
							}
						}
					}
				]
			},
			{
				columns: [
					{
						width: 6,
						field: {
							type: "field",
							path: ["intakeType"],
							label: messages.intakeType,
							errorMessages: genericErrorMessages,
							input: {
								type: "button-group",
								props: { labels: ivabradineIntakeTypeMessages }
							}
						}
					},
					{
						width: 6,
						field: {
							type: "field",
							path: ["dose"],
							label: messages.dose,
							errorMessages: genericErrorMessages,
							input: { type: "input", props: { type: "text" } }
						}
					}
				]
			}
		]
	},
	diuretic: {
		type: "field-group-list",
		path: ["diuretic"],
		label: messages.diuretic,
		rows: [
			{
				columns: [
					{
						width: 12,
						field: {
							type: "field",
							path: ["activeIngredient"],
							label: messages.activeIngredient,
							errorMessages: genericErrorMessages,
							input: { type: "input", props: { type: "text" } }
						}
					}
				]
			},
			{
				columns: [
					{
						width: 6,
						field: {
							type: "field",
							path: ["intakeType"],
							label: messages.intakeType,
							errorMessages: genericErrorMessages,
							input: {
								type: "button-group",
								props: { labels: diureticIntakeTypeMessages }
							}
						}
					},
					{
						width: 6,
						field: {
							type: "field",
							path: ["dose"],
							label: messages.dose,
							errorMessages: genericErrorMessages,
							input: { type: "input", props: { type: "text" } }
						}
					}
				]
			}
		]
	},
	antiarrhythmicAgent: {
		type: "field-group-list",
		path: ["antiarrhythmicAgent"],
		label: messages.antiarrhythmicAgent,
		rows: [
			{
				columns: [
					{
						width: 12,
						field: {
							type: "field",
							path: ["activeIngredient"],
							label: messages.activeIngredient,
							errorMessages: genericErrorMessages,
							input: { type: "input", props: { type: "text" } }
						}
					}
				]
			},
			{
				columns: [
					{
						width: 6,
						field: {
							type: "field",
							path: ["intakeType"],
							label: messages.intakeType,
							errorMessages: genericErrorMessages,
							input: {
								type: "button-group",
								props: {
									labels: antiarrhythmicAgentIntakeTypeMessages
								}
							}
						}
					},
					{
						width: 6,
						field: {
							type: "field",
							path: ["dose"],
							label: messages.dose,
							errorMessages: genericErrorMessages,
							input: { type: "input", props: { type: "text" } }
						}
					}
				]
			}
		]
	},
	anticoagulant: {
		type: "field-group-list",
		path: ["anticoagulant"],
		label: messages.anticoagulant,
		rows: [
			{
				columns: [
					{
						width: 12,
						field: {
							type: "field",
							path: ["activeIngredient"],
							label: messages.activeIngredient,
							errorMessages: genericErrorMessages,
							input: { type: "input", props: { type: "text" } }
						}
					}
				]
			},
			{
				columns: [
					{
						width: 6,
						field: {
							type: "field",
							path: ["intakeType"],
							label: messages.intakeType,
							errorMessages: genericErrorMessages,
							input: {
								type: "button-group",
								props: {
									labels: anticoagulantIntakeTypeMessages
								}
							}
						}
					},
					{
						width: 6,
						field: {
							type: "field",
							path: ["dose"],
							label: messages.dose,
							errorMessages: genericErrorMessages,
							input: { type: "input", props: { type: "text" } }
						}
					}
				]
			}
		]
	},
	trombocytaAggregationInhibitor: {
		type: "field-group-list",
		path: ["trombocytaAggregationInhibitor"],
		label: messages.trombocytaAggregationInhibitor,
		rows: [
			{
				columns: [
					{
						width: 12,
						field: {
							type: "field",
							path: ["activeIngredient"],
							label: messages.activeIngredient,
							errorMessages: genericErrorMessages,
							input: { type: "input", props: { type: "text" } }
						}
					}
				]
			},
			{
				columns: [
					{
						width: 6,
						field: {
							type: "field",
							path: ["intakeType"],
							label: messages.intakeType,
							errorMessages: genericErrorMessages,
							input: {
								type: "button-group",
								props: {
									labels: trombocytaAggregationInhibitorIntakeTypeMessages
								}
							}
						}
					},
					{
						width: 6,
						field: {
							type: "field",
							path: ["dose"],
							label: messages.dose,
							errorMessages: genericErrorMessages,
							input: { type: "input", props: { type: "text" } }
						}
					}
				]
			}
		]
	},
	statin: {
		type: "field-group-list",
		path: ["statin"],
		label: messages.statin,
		rows: [
			{
				columns: [
					{
						width: 12,
						field: {
							type: "field",
							path: ["activeIngredient"],
							label: messages.activeIngredient,
							errorMessages: genericErrorMessages,
							input: { type: "input", props: { type: "text" } }
						}
					}
				]
			},
			{
				columns: [
					{
						width: 6,
						field: {
							type: "field",
							path: ["intakeType"],
							label: messages.intakeType,
							errorMessages: genericErrorMessages,
							input: {
								type: "button-group",
								props: { labels: statinIntakeTypeMessages }
							}
						}
					},
					{
						width: 6,
						field: {
							type: "field",
							path: ["dose"],
							label: messages.dose,
							errorMessages: genericErrorMessages,
							input: { type: "input", props: { type: "text" } }
						}
					}
				]
			}
		]
	},
	allopurinol: {
		type: "field-group-list",
		path: ["allopurinol"],
		label: messages.allopurinol,
		rows: [
			{
				columns: [
					{
						width: 12,
						field: {
							type: "field",
							path: ["activeIngredient"],
							label: messages.activeIngredient,
							errorMessages: genericErrorMessages,
							input: {
								type: "select",
								props: {
									labels: allopurinolActiveIngredientMessages
								}
							}
						}
					}
				]
			},
			{
				columns: [
					{
						width: 6,
						field: {
							type: "field",
							path: ["intakeType"],
							label: messages.intakeType,
							errorMessages: genericErrorMessages,
							input: {
								type: "button-group",
								props: { labels: allopurinolIntakeTypeMessages }
							}
						}
					},
					{
						width: 6,
						field: {
							type: "field",
							path: ["dose"],
							label: messages.dose,
							errorMessages: genericErrorMessages,
							input: { type: "input", props: { type: "text" } }
						}
					}
				]
			}
		]
	},
	digitalis: {
		type: "field-group-list",
		path: ["digitalis"],
		label: messages.digitalis,
		rows: [
			{
				columns: [
					{
						width: 12,
						field: {
							type: "field",
							path: ["activeIngredient"],
							label: messages.activeIngredient,
							errorMessages: genericErrorMessages,
							input: { type: "input", props: { type: "text" } }
						}
					}
				]
			},
			{
				columns: [
					{
						width: 6,
						field: {
							type: "field",
							path: ["intakeType"],
							label: messages.intakeType,
							errorMessages: genericErrorMessages,
							input: {
								type: "button-group",
								props: { labels: digitalisIntakeTypeMessages }
							}
						}
					},
					{
						width: 6,
						field: {
							type: "field",
							path: ["dose"],
							label: messages.dose,
							errorMessages: genericErrorMessages,
							input: { type: "input", props: { type: "text" } }
						}
					}
				]
			}
		]
	},
	dopamine: {
		type: "field-group-list",
		path: ["dopamine"],
		label: messages.dopamine,
		rows: [
			{
				columns: [
					{
						width: 12,
						field: {
							type: "field",
							path: ["activeIngredient"],
							label: messages.activeIngredient,
							errorMessages: genericErrorMessages,
							input: {
								type: "select",
								props: {
									labels: dopamineActiveIngredientMessages
								}
							}
						}
					}
				]
			},
			{
				columns: [
					{
						width: 6,
						field: {
							type: "field",
							path: ["intakeType"],
							label: messages.intakeType,
							errorMessages: genericErrorMessages,
							input: {
								type: "button-group",
								props: { labels: dopamineIntakeTypeMessages }
							}
						}
					},
					{
						width: 6,
						field: {
							type: "field",
							path: ["dose"],
							label: messages.dose,
							errorMessages: genericErrorMessages,
							input: { type: "input", props: { type: "text" } }
						}
					}
				]
			}
		]
	},
	dobutamine: {
		type: "field-group-list",
		path: ["dobutamine"],
		label: messages.dobutamine,
		rows: [
			{
				columns: [
					{
						width: 12,
						field: {
							type: "field",
							path: ["activeIngredient"],
							label: messages.activeIngredient,
							errorMessages: genericErrorMessages,
							input: {
								type: "select",
								props: {
									labels: dobutamineActiveIngredientMessages
								}
							}
						}
					}
				]
			},
			{
				columns: [
					{
						width: 6,
						field: {
							type: "field",
							path: ["intakeType"],
							label: messages.intakeType,
							errorMessages: genericErrorMessages,
							input: {
								type: "button-group",
								props: { labels: dobutamineIntakeTypeMessages }
							}
						}
					},
					{
						width: 6,
						field: {
							type: "field",
							path: ["dose"],
							label: messages.dose,
							errorMessages: genericErrorMessages,
							input: { type: "input", props: { type: "text" } }
						}
					}
				]
			}
		]
	},
	norepinephrine: {
		type: "field-group-list",
		path: ["norepinephrine"],
		label: messages.norepinephrine,
		rows: [
			{
				columns: [
					{
						width: 12,
						field: {
							type: "field",
							path: ["activeIngredient"],
							label: messages.activeIngredient,
							errorMessages: genericErrorMessages,
							input: {
								type: "select",
								props: {
									labels: norepinephrineActiveIngredientMessages
								}
							}
						}
					}
				]
			},
			{
				columns: [
					{
						width: 6,
						field: {
							type: "field",
							path: ["intakeType"],
							label: messages.intakeType,
							errorMessages: genericErrorMessages,
							input: {
								type: "button-group",
								props: {
									labels: norepinephrineIntakeTypeMessages
								}
							}
						}
					},
					{
						width: 6,
						field: {
							type: "field",
							path: ["dose"],
							label: messages.dose,
							errorMessages: genericErrorMessages,
							input: { type: "input", props: { type: "text" } }
						}
					}
				]
			}
		]
	},
	pdeInhibitor: {
		type: "field-group-list",
		path: ["pdeInhibitor"],
		label: messages.pdeInhibitor,
		rows: [
			{
				columns: [
					{
						width: 12,
						field: {
							type: "field",
							path: ["activeIngredient"],
							label: messages.activeIngredient,
							errorMessages: genericErrorMessages,
							input: {
								type: "select",
								props: {
									labels: pdeInhibitorActiveIngredientMessages
								}
							}
						}
					}
				]
			},
			{
				columns: [
					{
						width: 6,
						field: {
							type: "field",
							path: ["intakeType"],
							label: messages.intakeType,
							errorMessages: genericErrorMessages,
							input: {
								type: "button-group",
								props: {
									labels: pdeInhibitorIntakeTypeMessages
								}
							}
						}
					},
					{
						width: 6,
						field: {
							type: "field",
							path: ["dose"],
							label: messages.dose,
							errorMessages: genericErrorMessages,
							input: { type: "input", props: { type: "text" } }
						}
					}
				]
			}
		]
	},
	levosimendan: {
		type: "field-group-list",
		path: ["levosimendan"],
		label: messages.levosimendan,
		rows: [
			{
				columns: [
					{
						width: 12,
						field: {
							type: "field",
							path: ["activeIngredient"],
							label: messages.activeIngredient,
							errorMessages: genericErrorMessages,
							input: {
								type: "select",
								props: {
									labels: levosimendanActiveIngredientMessages
								}
							}
						}
					}
				]
			},
			{
				columns: [
					{
						width: 6,
						field: {
							type: "field",
							path: ["intakeType"],
							label: messages.intakeType,
							errorMessages: genericErrorMessages,
							input: {
								type: "button-group",
								props: {
									labels: levosimendanIntakeTypeMessages
								}
							}
						}
					},
					{
						width: 6,
						field: {
							type: "field",
							path: ["dose"],
							label: messages.dose,
							errorMessages: genericErrorMessages,
							input: { type: "input", props: { type: "text" } }
						}
					}
				]
			}
		]
	},
	antibiotic: {
		type: "field-group-list",
		path: ["antibiotic"],
		label: messages.antibiotic,
		rows: [
			{
				columns: [
					{
						width: 12,
						field: {
							type: "field",
							path: ["activeIngredient"],
							label: messages.activeIngredient,
							errorMessages: genericErrorMessages,
							input: { type: "input", props: { type: "text" } }
						}
					}
				]
			},
			{
				columns: [
					{
						width: 6,
						field: {
							type: "field",
							path: ["intakeType"],
							label: messages.intakeType,
							errorMessages: genericErrorMessages,
							input: {
								type: "button-group",
								props: { labels: antibioticIntakeTypeMessages }
							}
						}
					},
					{
						width: 6,
						field: {
							type: "field",
							path: ["dose"],
							label: messages.dose,
							errorMessages: genericErrorMessages,
							input: { type: "input", props: { type: "text" } }
						}
					}
				]
			}
		]
	},
	other: {
		type: "field-group-list",
		path: ["other"],
		label: messages.other,
		rows: [
			{
				columns: [
					{
						width: 12,
						field: {
							type: "field",
							path: ["activeIngredient"],
							label: messages.activeIngredient,
							errorMessages: genericErrorMessages,
							input: { type: "input", props: { type: "text" } }
						}
					}
				]
			},
			{
				columns: [
					{
						width: 6,
						field: {
							type: "field",
							path: ["intakeType"],
							label: messages.intakeType,
							errorMessages: genericErrorMessages,
							input: {
								type: "button-group",
								props: { labels: otherIntakeTypeMessages }
							}
						}
					},
					{
						width: 6,
						field: {
							type: "field",
							path: ["dose"],
							label: messages.dose,
							errorMessages: genericErrorMessages,
							input: { type: "input", props: { type: "text" } }
						}
					}
				]
			}
		]
	}
};

const fieldset: FieldsetData = {
	rows: [
		{ columns: [{ width: 12, field: fields.acei }] },
		{ columns: [{ width: 12, field: fields.bbl }] },
		{ columns: [{ width: 12, field: fields.arb }] },
		{ columns: [{ width: 12, field: fields.mra }] },
		{ columns: [{ width: 12, field: fields.arni }] },
		{ columns: [{ width: 12, field: fields.ivabradine }] },
		{ columns: [{ width: 12, field: fields.diuretic }] },
		{ columns: [{ width: 12, field: fields.antiarrhythmicAgent }] },
		{ columns: [{ width: 12, field: fields.anticoagulant }] },
		{
			columns: [
				{ width: 12, field: fields.trombocytaAggregationInhibitor }
			]
		},
		{ columns: [{ width: 12, field: fields.statin }] },
		{ columns: [{ width: 12, field: fields.allopurinol }] },
		{ columns: [{ width: 12, field: fields.digitalis }] },
		{ columns: [{ width: 12, field: fields.dopamine }] },
		{ columns: [{ width: 12, field: fields.dobutamine }] },
		{ columns: [{ width: 12, field: fields.norepinephrine }] },
		{ columns: [{ width: 12, field: fields.pdeInhibitor }] },
		{ columns: [{ width: 12, field: fields.levosimendan }] },
		{ columns: [{ width: 12, field: fields.antibiotic }] },
		{ columns: [{ width: 12, field: fields.other }] }
	]
};

export const node: LeafNode = {
	id: "heartFailureMedicalTherapy",
	value: {
		title: messages.heartFailureMedicalTherapy,
		validator,
		fieldset,
		isImportant: true,
		applyCurrentDate: true,
		defaults: [
			{
				path: ["acei"],
				query: { interpretation: { vocab: "htx", code: "acei" } }
			},
			{
				path: ["bbl"],
				query: { interpretation: { vocab: "htx", code: "bbl" } }
			},
			{
				path: ["arb"],
				query: { interpretation: { vocab: "htx", code: "arb" } }
			},
			{
				path: ["mra"],
				query: { interpretation: { vocab: "htx", code: "mra" } }
			},
			{
				path: ["arni"],
				query: { interpretation: { vocab: "htx", code: "arni" } }
			},
			{
				path: ["ivabradine"],
				query: { interpretation: { vocab: "htx", code: "ivabradine" } }
			},
			{
				path: ["diuretic"],
				query: { interpretation: { vocab: "htx", code: "diuretic" } }
			},
			{
				path: ["antiarrhythmicAgent"],
				query: {
					interpretation: {
						vocab: "htx",
						code: "antiarrhythmic_agent"
					}
				}
			},
			{
				path: ["anticoagulant"],
				query: {
					interpretation: { vocab: "htx", code: "anticoagulant" }
				}
			},
			{
				path: ["trombocytaAggregationInhibitor"],
				query: {
					interpretation: {
						vocab: "htx",
						code: "trombocyta_aggregation_inhibitor"
					}
				}
			},
			{
				path: ["statin"],
				query: { interpretation: { vocab: "htx", code: "statin" } }
			},
			{
				path: ["allopurinol"],
				query: { interpretation: { vocab: "htx", code: "allopurinol" } }
			},
			{
				path: ["digitalis"],
				query: { interpretation: { vocab: "htx", code: "digitalis" } }
			},
			{
				path: ["dopamine"],
				query: { interpretation: { vocab: "htx", code: "dopamine" } }
			},
			{
				path: ["dobutamine"],
				query: { interpretation: { vocab: "htx", code: "dobutamine" } }
			},
			{
				path: ["norepinephrine"],
				query: {
					interpretation: {
						vocab: "htx",
						code: "adrenalin_or_noradrenalin"
					}
				}
			},
			{
				path: ["pdeInhibitor"],
				query: {
					interpretation: { vocab: "htx", code: "pde_inhibitor" }
				}
			},
			{
				path: ["levosimendan"],
				query: {
					interpretation: { vocab: "htx", code: "levosimendan" }
				}
			},
			{
				path: ["antibiotic"],
				query: { interpretation: { vocab: "htx", code: "antibiotic" } }
			},
			{
				path: ["other"],
				query: {
					interpretation: {
						vocab: "htx",
						code: "heart_failure_medical_therapy_other_medicine"
					}
				}
			}
		]
	}
};
